// src/pages/qbo/WriteBackCard.tsx
//
// Advantage write-back (agent 3.26.0): the agent stamps Advantage rows when
// they reach QuickBooks — InHead.SentToQB / Qbdate for documents, Cash.Deposit
// / Depositdate / Depositno for receipts — so the ERP side sees what is in
// QuickBooks. Every push stamps its own rows; this card runs the one-off
// backfill for everything pushed before 3.26.0.

import { useState } from 'react';
import qboApi from '../../services/qboApi';
import SyncResultList from './SyncResultList';
import type { SyncResult } from './qboTypes';
import { runJob, type JobView } from './qboJobs';

interface Props { onMessage: (type: 'error' | 'success' | 'info', text: string) => void }

interface Plan { documentsToStamp: number; documentsAlreadyStamped: number; receiptsToStamp: number; receiptsAlreadyStamped: number; sampleDocuments: string[]; sampleReceipts: string[]; enabled: boolean; notes: string[] }

export default function WriteBackCard({ onMessage }: Props) {
  const [plan, setPlan] = useState<Plan | null>(null);
  const [planning, setPlanning] = useState(false);
  const [running, setRunning] = useState(false);
  const [job, setJob] = useState<JobView | null>(null);
  const [result, setResult] = useState<SyncResult | null>(null);
  const [force, setForce] = useState(false);   // 3.26.1: re-stamp flagged rows too (repairs dates)

  const doPlan = async () => {
    setPlanning(true); setPlan(null); setResult(null);
    try { setPlan((await qboApi.writeBackPlan(force)) as Plan); }
    catch (e: unknown) { onMessage('error', (e as Error).message); }
    finally { setPlanning(false); }
  };
  const doRun = async () => {
    setRunning(true); setResult(null); setJob(null);
    try {
      const r = await runJob(() => qboApi.writeBackRun(force), setJob);
      setResult(r);
      onMessage(r.errors ? 'error' : 'success', `Advantage write-back: ${r.updated} document row(s), ${r.created} receipt row(s) stamped, ${r.errors} error(s)`);
      setPlan(null);
    } catch (e: unknown) { onMessage('error', (e as Error).message); }
    finally { setRunning(false); setJob(null); }
  };
  const todo = plan ? plan.documentsToStamp + plan.receiptsToStamp : 0;

  return (
    <div className="bg-slate-800 rounded-xl p-5 border border-slate-700">
      <div className="text-sm font-semibold text-slate-400">Advantage write-back</div>
      <div className="text-[11px] text-slate-500 mt-0.5">Advantage learns what reached QuickBooks: documents get SentToQB / Qbdate, receipts get Deposit / Depositdate / Depositno (the QuickBooks id). Every push stamps its own rows; the backfill stamps what was pushed before.</div>
      <div className="flex flex-wrap items-center gap-2 mt-3">
        <label className="text-[11px] text-slate-400 inline-flex items-center gap-1"><input type="checkbox" checked={force} onChange={(e) => { setForce(e.target.checked); setPlan(null); }} /> re-stamp rows already flagged (rewrites the dates from the sync log)</label>
        <button className="px-3 py-1.5 rounded-lg text-xs font-medium border border-slate-600 text-slate-200 hover:bg-slate-700 transition-colors disabled:opacity-50" onClick={doPlan} disabled={planning || running}>{planning ? 'Counting…' : 'Count'}</button>
        {plan && todo > 0 && plan.enabled && (
          <button className="px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-500 transition-colors disabled:opacity-50" onClick={doRun} disabled={running}>
            {running ? (job ? `Stamping ${job.done}/${job.total}…` : 'Starting…') : `Stamp ${plan.documentsToStamp} document(s) + ${plan.receiptsToStamp} receipt(s)`}
          </button>
        )}
      </div>
      {plan && (
        <div className="mt-3 text-xs text-slate-300 space-y-1">
          <div>Documents: <span className="text-white">{plan.documentsToStamp}</span> to stamp, {plan.documentsAlreadyStamped} already flagged{plan.sampleDocuments.length > 0 && <span className="text-slate-500"> · e.g. {plan.sampleDocuments.join(', ')}</span>}</div>
          <div>Receipts: <span className="text-white">{plan.receiptsToStamp}</span> to stamp, {plan.receiptsAlreadyStamped} already flagged{plan.sampleReceipts.length > 0 && <span className="text-slate-500"> · e.g. {plan.sampleReceipts.join(', ')}</span>}</div>
          {!plan.enabled && <div className="text-amber-300">Write-back is disabled on this agent.</div>}
          <ul className="text-[11px] text-slate-500 list-disc pl-4">{plan.notes.map((n, i) => <li key={i}>{n}</li>)}</ul>
        </div>
      )}
      <SyncResultList result={result} title="Write-back" />
    </div>
  );
}
